import type {
    ActorEvent,
    ButtonItem,
    HighLightConditions,
    RecordLikeProtocol,
    RichTextContentProtocol,
    SelectedMode,
    TableColumnWidth,
    TableRowHeightProtocol,
    TableViewOptions,
    ViewField
} from '@lighthouse/core'
import { useApplicationContext } from '@lighthouse/shared'
import { equals, sortBy } from 'rambda'
import type { RefObject } from 'react'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { useViewBlockContext } from '../../context'
import { TableContainer } from './TableContainer'
import TableHeader from './TableHeader'

interface TableProps {
    recordOperatorActions?: ButtonItem[]
    checkable?: boolean
    viewFields: ViewField[]
    selectedRecords?: string[]
    highLightRules?: HighLightConditions
    scrollContainerRef: RefObject<HTMLDivElement>
    blockWidth?: number
    tableRowHeight?: TableRowHeightProtocol
    designConfig: TableViewOptions
    onRecordSelect?: (recordIds: string[]) => void
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record: RecordLikeProtocol) => Promise<void>
    recordClickable?: boolean
    onRenderButtonTitle: (v: RichTextContentProtocol) => string
    onSelectModeChange?: (mode?: SelectedMode) => void
    onTableColumnWidthChange?: (val: TableColumnWidth) => void
}

const SCxTableContainer = styled.div`
    background-color: var(--color-white);
`

export const Table: React.FC<TableProps> = ({
    checkable,
    recordOperatorActions,
    viewFields,
    selectedRecords,
    highLightRules,
    scrollContainerRef,
    blockWidth,
    tableRowHeight,
    designConfig,
    onRecordSelect,
    onRecordOperatorActionTrigger,
    onRecordClick,
    onRenderButtonTitle,
    onSelectModeChange,
    onTableColumnWidthChange,
    recordClickable
}) => {
    const { dataSource, recordPoints } = useViewBlockContext()
    const { hiddenHeader } = designConfig
    const { isMobile } = useApplicationContext()
    const noRecords = recordPoints.length === 0
    const { schema } = dataSource

    const allRecordSelected = useMemo(() => {
        return (
            selectedRecords &&
            !noRecords &&
            equals(
                sortBy(a => a, selectedRecords),
                sortBy(
                    a => a,
                    recordPoints.map(p => p.id)
                )
            )
        )
    }, [noRecords, recordPoints, selectedRecords])

    const handleAllRecordSelect = useCallback(
        (selected: boolean) => {
            if (selected) {
                onRecordSelect?.(recordPoints.map(p => p.id))
            } else {
                onRecordSelect?.([])
            }
        },
        [onRecordSelect, recordPoints]
    )

    return (
        <SCxTableContainer className="tableContainer">
            {!hiddenHeader && (
                <TableHeader
                    checkable={checkable}
                    viewFields={viewFields}
                    isMobile={isMobile}
                    schema={schema}
                    allRecordSelected={allRecordSelected}
                    designConfig={designConfig}
                    onAllRecordSelect={handleAllRecordSelect}
                    onSelectModeChange={onSelectModeChange}
                    onTableColumnWidthChange={onTableColumnWidthChange}
                />
            )}

            <TableContainer
                checkable={checkable}
                recordOperatorActions={recordOperatorActions}
                viewFields={viewFields}
                isMobile={isMobile}
                blockWidth={blockWidth}
                scrollRef={scrollContainerRef}
                selectedRecords={selectedRecords}
                highLightRules={highLightRules}
                tableRowHeight={tableRowHeight}
                designConfig={designConfig}
                onRecordSelect={onRecordSelect}
                onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                onRecordClick={onRecordClick}
                onRenderButtonTitle={onRenderButtonTitle}
                recordClickable={recordClickable}
            />
        </SCxTableContainer>
    )
}
